import { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { ClientContext, useManualQuery } from 'graphql-hooks'

import { getCompanyAndUserInfo } from '../../../../graphql/custom-queries'
import { getCompanyAndUser } from '../../../../services/api/companies'
import { updateSlackToken } from '../../../../services/api/slack'
import { useAppDispatch } from '../../../../store/hooks'
import { setUserId } from '../../../../store/user-id-slice'
import { setAuthState } from '../../../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { confirmSigninWithCustomChallengeAnswer, IAuthNextStep, signin } from '@vacationtracker/shared/services/auth'

import { IGetCompanyAndUserInfo } from '../../../../types/custom-queries'
import { FrontendUrls } from '../../../../types/urls'
import { rememberProviderAccount, saveRememberedLogin } from '../../../../util/remembered-login'
import { forgetAccessRequest } from '../../../../util/access-requests'
import { updateGraphQlAuthHeaderAndSubscription } from '../../../../util/update-graphql-header-and-subscription'
import { trackLoginCompleted, trackSignup } from '../analytics'

export interface IPlatformTokenSigninParams {
  username: string
  token: string
  platform: string
  email: string
  /** Slack only, and optional: the server's copy of the tokens is refreshed when it is there. */
  botToken?: string
}

export interface ISignupSession {
  /** Resolves true when a session was established, false when Cognito had no account to open. */
  signinWithPlatformToken: (params: IPlatformTokenSigninParams) => Promise<boolean>
}

/**
 * An existing account arriving through the sign-up page. This is the only place here that
 * writes to the shared store, and only once Cognito has answered, so an abandoned or failed
 * attempt leaves the rest of the app untouched.
 */
export const useSignupSession = (): ISignupSession => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const gqlClient = useContext(ClientContext)
  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const signinWithPlatformToken = async ({ username, token, platform, email, botToken }: IPlatformTokenSigninParams) => {
    const signInResponse = (await signin(username)) as IAuthNextStep
    if (signInResponse.challengeName !== 'CUSTOM_CHALLENGE' || signInResponse.challengeParam?.question !== 'token') {
      return false
    }

    const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(token, { loginType: platform })
    localStorage.setItem('userId', cognitoResponse.username)

    try {
      // The shared helper, which also attaches the AppSync subscription client. Setting only
      // the header left the dashboard without realtime until App re-ran on the user id.
      if (gqlClient) {
        await updateGraphQlAuthHeaderAndSubscription({ graphQlClient: gqlClient })
      }
    } catch (error) {
      // Swallow the error
    }

    // Keeps the server's copy of the tokens level with the ones this sign-in just produced,
    // which is what the sign-in page does on the same path.
    if (platform === PlatformEnum.Slack) {
      // Not awaited: nothing below depends on it and its failure is already ignored, so making
      // the visitor wait a round-trip for it only delays the dashboard.
      updateSlackToken(token, botToken).catch(() => undefined)
    }

    dispatch(setUserId(cognitoResponse.username))
    // So a later logout leaves the sign-in page offering this account rather than whichever
    // one happened to be used last.
    if (email) {
      saveRememberedLogin({ email, platform: platform as PlatformEnum })
      rememberProviderAccount({ email, platform })
      forgetAccessRequest(email)
    }
    await getCompanyAndUser(cognitoResponse.username, getCompanyAndUserInfoQuery, dispatch, history)
    trackLoginCompleted(platform)
    // Every path into this hook is the same story: someone came to sign up, already had an
    // account, and was signed in instead. That ends the sign-up funnel somewhere other than
    // SIGNUP_WITH_<PLATFORM>_SUCCESS, so it needs its own name rather than looking like churn.
    trackSignup('SIGNUP_EXISTING_ACCOUNT_SIGNED_IN', { platform })
    dispatch(setAuthState(AuthStateEnum.signedIn))
    history.push(FrontendUrls.dashboard)

    return true
  }

  return { signinWithPlatformToken }
}
